import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DT_TYPES } from "@/lib/daytime";

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Promos({ s, setS }) {
  const ps = s.promos || [];
  const upd = (i, k, v) => setS({ ...s, promos: ps.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  return (
    <div data-testid="dt-promos">
      <Label className="text-xs">Promo codes</Label>
      {ps.map((p, i) => (
        <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_110px_80px_140px_auto] gap-2 mt-1 items-center">
          <Input value={p.code} onChange={(e) => upd(i, "code", e.target.value.toUpperCase())} placeholder="CODE" className="rounded-xl" data-testid={`promo-code-${i}`} />
          <select value={p.kind} onChange={(e) => upd(i, "kind", e.target.value)} className="h-10 rounded-xl border px-2 text-sm"><option value="percent">% off</option><option value="amount">$ off</option></select>
          <Input type="number" value={p.value} onChange={(e) => upd(i, "value", e.target.value)} className="rounded-xl" data-testid={`promo-value-${i}`} />
          <Input type="date" value={p.expires || ""} onChange={(e) => upd(i, "expires", e.target.value)} className="rounded-xl" />
          <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={p.active} onChange={(e) => upd(i, "active", e.target.checked)} /> on
            <button onClick={() => setS({ ...s, promos: ps.filter((_, j) => j !== i) })} className="text-coral ml-2">✕</button></label>
        </div>))}
      <Button size="sm" variant="outline" className="rounded-full mt-2" onClick={() => setS({ ...s, promos: [...ps, { code: "", kind: "percent", value: 10, active: true, expires: "" }] })} data-testid="promo-add">+ Add promo code</Button>
      <p className="text-[11px] text-slate-400 mt-1">Promo discounts apply to passes and supervision, not transport.</p>
    </div>
  );
}

export function DaytimeSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/daytime/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  if (!s) return null;
  const owner = user?.role === "admin";
  const save = async () => { try { const { data } = await api.put("/daytime/admin/settings", s); setS(data); toast.success("Daytime settings saved — pricing updated everywhere"); } catch (e) { toast.error(apiErr(e)); } };
  const num = (k, label, t) => (
    <div><Label className="text-xs">{label}</Label><Input type="number" value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="rounded-xl" data-testid={t} /></div>
  );
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-3 space-y-5" data-testid="dt-settings">
      <div>
        <p className="font-display font-extrabold text-navy mb-2">Pricing</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.keys(s.prices).map((k) => (
            <div key={k}><Label className="text-xs">{DT_TYPES[k]} ($)</Label>
              <Input type="number" value={s.prices[k]} onChange={(e) => setS({ ...s, prices: { ...s.prices, [k]: e.target.value } })} className="rounded-xl" data-testid={`dt-price-${k}`} /></div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {num("capacity_default", "Facility capacity (at one time)", "dt-capacity-default")}
        {num("supervised_capacity", "Supervised care capacity", "dt-sup-capacity")}
        {num("booking_cutoff_minutes", "Booking cutoff (min before arrival)", "dt-booking-cutoff")}
        <div><Label className="text-xs">Opens</Label><Input type="time" value={s.open_time} onChange={(e) => setS({ ...s, open_time: e.target.value })} className="rounded-xl" data-testid="dt-open-time" /></div>
        <div><Label className="text-xs">Closes</Label><Input type="time" value={s.close_time} onChange={(e) => setS({ ...s, close_time: e.target.value })} className="rounded-xl" data-testid="dt-close-time" /></div>
        <div><Label className="text-xs">Little Rocker hours</Label><Input type="number" value={s.durations.pass2} onChange={(e) => setS({ ...s, durations: { ...s.durations, pass2: e.target.value } })} className="rounded-xl" data-testid="dt-dur-pass2" /></div>
        <div><Label className="text-xs">Adventure Pass hours</Label><Input type="number" value={s.durations.pass4} onChange={(e) => setS({ ...s, durations: { ...s.durations, pass4: e.target.value } })} className="rounded-xl" data-testid="dt-dur-pass4" /></div>
      </div>
      <div><Label className="text-xs">Arrival time slots (comma separated, 24h)</Label>
        <Input value={(s.arrival_slots || []).join(", ")} onChange={(e) => setS({ ...s, arrival_slots: e.target.value.split(",").map((x) => x.trim()) })} className="rounded-xl" data-testid="dt-slots" /></div>
      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={s.work_rock_enabled} onChange={(e) => setS({ ...s, work_rock_enabled: e.target.checked })} data-testid="dt-wr-enabled" /> Work & Rock bookable online</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={s.rock_run_enabled} onChange={(e) => setS({ ...s, rock_run_enabled: e.target.checked })} data-testid="dt-rr-enabled" /> Rock & Run bookable online</label>
      </div>
      <div><Label className="text-xs">Member benefits (one per line)</Label>
        <Textarea rows={5} value={(s.member_benefits || []).join("\n")} onChange={(e) => setS({ ...s, member_benefits: e.target.value.split("\n") })} className="rounded-xl" data-testid="dt-benefits" /></div>
      <div><Label className="text-xs">Parking reminder (in confirmation email)</Label>
        <Input value={s.parking_note} onChange={(e) => setS({ ...s, parking_note: e.target.value })} className="rounded-xl" data-testid="dt-parking" /></div>
      <Promos s={s} setS={setS} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {num("cancel_cutoff_hours", "Cancellation cutoff (hours)", "dt-cancel-cutoff")}
        {num("pass_valid_days", "Rock Pass valid (days)", "dt-pass-valid")}
        {num("booking_window_days", "Bookable days ahead", "dt-window")}
      </div>
      <div className="max-w-xs"><Label className="text-xs">Weekdays (Mon–Fri) open from</Label>
        <Input type="date" value={s.weekday_start || ""} onChange={(e) => setS({ ...s, weekday_start: e.target.value })} className="rounded-xl" data-testid="dt-weekday-start" />
        <p className="text-[11px] text-slate-400 mt-1">Weekends are bookable now; weekdays before this date stay closed unless you open them on Dates.</p></div>
      <div>
        <Label className="text-xs">Membership period</Label>
        <div className="flex gap-2 mt-1">
          {[["rolling", "Rolling 30 days"], ["calendar", "Calendar month"]].map(([k, l]) => (
            <button key={k} onClick={() => setS({ ...s, membership_period: k })} data-testid={`dt-period-${k}`} className={`px-4 py-2 rounded-full border text-sm ${s.membership_period === k ? "bg-navy text-white border-navy" : "border-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs">Open by default on</Label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {WD.map((w, i) => {
            const on = s.open_weekdays.includes(i);
            return <button key={w} onClick={() => setS({ ...s, open_weekdays: on ? s.open_weekdays.filter((x) => x !== i) : [...s.open_weekdays, i] })} data-testid={`dt-weekday-${i}`}
              className={`w-12 py-2 rounded-xl border text-sm ${on ? "bg-turquoise/20 border-turquoise text-navy font-bold" : "border-slate-200 text-slate-400"}`}>{w}</button>;
          })}
        </div>
      </div>
      {owner ? <Button onClick={save} className="rounded-full bg-navy font-bold" data-testid="dt-settings-save">Save Daytime settings</Button>
        : <p className="text-xs text-slate-400">Only the owner can change Daytime pricing and rules.</p>}
    </div>
  );
}
